<template>
  <div class="column-visibility-dropdown">
    <themed-button
      ref="toggleButton"
      variant="outline"
      size="icon"
      @click="toggleDropdown"
      :disabled="disabled"
      aria-label="Show/hide columns"
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-haspopup="true"
      title="Show Columns"
    >
      <theme-icon icon="columns" type="icons" classes="flex" />
    </themed-button>

    <div
      v-if="isOpen"
      ref="dropdown"
      class="column-visibility-dropdown__menu"
      role="menu"
      aria-label="Column visibility options"
    >
      <div class="column-visibility-dropdown__header">
        Show Columns
      </div>
      
      <div class="column-visibility-dropdown__list">
        <label
          v-for="column in selectableColumns"
          :key="column.field"
          class="column-visibility-dropdown__item"
          role="menuitemcheckbox"
          :aria-checked="isColumnVisible(column.field) ? 'true' : 'false'"
        >
          <input
            type="checkbox"
            :checked="isColumnVisible(column.field)"
            :disabled="isColumnRequired(column.field)"
            @change="handleColumnToggle(column.field, $event.target.checked)"
            class="column-visibility-dropdown__checkbox"
          />
          <span class="column-visibility-dropdown__label">
            {{ column.label || column.field }}
          </span>
        </label>
      </div>
    </div>

    <!-- Backdrop to close dropdown when clicking outside -->
    <div
      v-if="isOpen"
      class="column-visibility-dropdown__backdrop"
      @click="closeDropdown"
    ></div>
  </div>
</template>

<script>
import ThemedButton from '../ThemedButton.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'ColumnVisibilityDropdown',
  components: {
    ThemedButton,
    ThemeIcon
  },
  props: {
    columns: {
      type: Array,
      required: true
      // Format: [{ field, label, showInTable, alwaysVisible }]
    },
    visibleColumns: {
      type: Array,
      default: () => []
      // Array of field names that are currently visible
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false
    };
  },
  computed: {
    selectableColumns() {
      // Filter out action columns and columns that shouldn't be in the selector
      return this.columns.filter(col => 
        col.type !== 'actions' && col.hideable !== false
      );
    }
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      
      if (this.isOpen) {
        // Add keyboard listener when dropdown opens
        this.$nextTick(() => {
          document.addEventListener('keydown', this.handleKeydown);
        });
      } else {
        // Remove keyboard listener when dropdown closes
        document.removeEventListener('keydown', this.handleKeydown);
      }
    },
    
    closeDropdown() {
      this.isOpen = false;
      document.removeEventListener('keydown', this.handleKeydown);
    },
    
    isColumnVisible(field) {
      return this.visibleColumns && this.visibleColumns.includes(field);
    },
    
    isColumnRequired(field) {
      // Check if column is marked as always visible
      const column = this.columns.find(col => col.field === field);
      return column && column.alwaysVisible === true;
    },
    
    handleColumnToggle(field, checked) {
      this.$emit('column-toggle', { field, visible: checked });
    },
    
    handleKeydown(event) {
      // Close dropdown on Escape key
      if (event.key === 'Escape') {
        this.closeDropdown();
        // Return focus to toggle button
        this.$refs.toggleButton.$el.focus();
      }
    }
  },
  beforeDestroy() {
    // Clean up event listener
    document.removeEventListener('keydown', this.handleKeydown);
  }
}
</script>
